import type { CSSProperties } from "react";

/**
 * Convert a served affiliate Theme's SurveyJS `cssVariables` blob into inline
 * overrides for the v2 design tokens.
 *
 * The result is spread onto the `.ui-v2-theme` wrapper's `style` prop during
 * render. ViewLoader already blocks on the theme query before mounting the
 * wrapper, so applying the tokens as inline style at render time is what
 * guarantees no colour flicker on load: a useEffect + setProperty (or a
 * document.documentElement write) would paint one frame of the default palette
 * first, and a :root override would also break the .ui-v2-theme scoping.
 * Inline style beats the stylesheet, and because only the raw custom
 * properties are overridden the affiliate colours survive a light/dark
 * switch: tokens.css flips those same properties through the `.dark` class,
 * and the inline values win over whichever palette is active.
 *
 * The Theme `configuration` column is an unvalidated TEXT blob, so every
 * source value is treated as hostile: anything unparseable omits its token and
 * the stylesheet default wins. Never throws.
 */
export function applyAffiliateTheme(
  cssVariables: Record<string, string | number> | undefined,
): CSSProperties {
  const style: Record<string, string> = {};

  const backcolor = parseHexColor(cssVariables?.["--sjs-primary-backcolor"]);
  if (backcolor) {
    const triplet = hexToHslTriplet(backcolor);
    style["--primary"] = triplet;
    // The focus ring is the brand colour in admin-v2 too.
    style["--ring"] = triplet;
    style["--primary-foreground"] = primaryForeground(backcolor, cssVariables);
  }

  const radius = cssVariables?.["--sjs-border-radius"];
  // 0 is a legitimate radius; only missing/non-numeric values omit the token.
  if (typeof radius === "number" && Number.isFinite(radius)) {
    style["--radius"] = `${radius}px`;
  }

  return style as CSSProperties;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse a 3- or 6-digit hex colour, with or without leading '#', case
 * insensitive. Returns null for anything else (undefined, wrong length,
 * non-hex characters); callers must treat null as "use the default".
 */
function parseHexColor(value: string | number | undefined): Rgb | null {
  if (typeof value !== "string") {
    return null;
  }
  const hex = value.trim().replace(/^#/, "");
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) {
    return null;
  }
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  const n = Number.parseInt(full, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

/**
 * Convert an RGB colour (0-255 channels) to the space-separated HSL triplet
 * the v2 tokens expect: "H S% L%", no hsl() wrapper — `@theme inline` wraps it
 * as hsl(var(--primary)), and --shadow-glow adds an alpha the same way.
 */
function hexToHslTriplet({ r, g, b }: Rgb): string {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (delta !== 0) {
    if (max === rn) {
      h = 60 * (((gn - bn) / delta) % 6);
    } else if (max === gn) {
      h = 60 * ((bn - rn) / delta + 2);
    } else {
      h = 60 * ((rn - gn) / delta + 4);
    }
    if (h < 0) {
      h += 360;
    }
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * The WCAG contrast ratio of white against luminance L is 1.05/(L+0.05); of
 * black it is (L+0.05)/0.05. The cutover is where the two are equal:
 * (L+0.05)^2 = 1.05*0.05, so L = sqrt(0.0525) - 0.05 = 0.179129. Picking the
 * better candidate by this threshold guarantees the best achievable contrast,
 * which at the threshold itself is ~4.58:1 — above the WCAG AA 4.5:1 floor.
 * A naive 0.5 midpoint is wrong: it picks white for luminances 0.179-0.5,
 * where white fails AA (e.g. #efaf00, a real affiliate colour, at 1.94:1).
 */
const FOREGROUND_LUMINANCE_THRESHOLD = Math.sqrt(0.0525) - 0.05;

/**
 * `--sjs-primary-forecolor` exists in only one fixture, so it cannot be relied
 * on. When absent, pick black or white by WCAG sRGB relative luminance of the
 * brand background: white on a light brand colour (#ffd600 is a real affiliate
 * value) is unreadable, so this is an accessibility floor.
 */
function primaryForeground(
  backcolor: Rgb,
  cssVariables: Record<string, string | number> | undefined,
): string {
  const forecolor = parseHexColor(cssVariables?.["--sjs-primary-forecolor"]);
  if (forecolor) {
    return hexToHslTriplet(forecolor);
  }
  return relativeLuminance(backcolor) > FOREGROUND_LUMINANCE_THRESHOLD
    ? "0 0% 0%"
    : "0 0% 100%";
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const linear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}
